import { useRef, useState, useEffect } from "react";
import { FaSquare, FaImage, FaLink, FaCamera } from "react-icons/fa";
import { toast } from "react-toastify";
import NoIndex from "../../../../../components/NoIndex";

const Toolbar = ({ elements, setElements, selectedId, previewMode }) => {
  const fileInputRef = useRef(null);
  const bgImageRef = useRef(null);

  const toolbarRef = useRef(null);
  const popupRef = useRef(null);

  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [linkValue, setLinkValue] = useState("");

  const [showImagePopup, setShowImagePopup] = useState(false);
  const [imageURL, setImageURL] = useState("");

  const [showBgPopup, setShowBgPopup] = useState(false);
  const [bgURL, setBgURL] = useState("");

  const fileToDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = () => {
        reject(new Error("Unable to read image file"));
      };

      reader.readAsDataURL(file);
    });
  };

  /* =========================================================
     GET SELECTED BOX
  ========================================================= */

  const getSelectedBox = () => {
    return elements.find((el) => el.id === selectedId && el.type === "box");
  };

  /* =========================================================
     CLOSE ALL POPUPS
  ========================================================= */

  const closeAllPopups = () => {
    setShowImagePopup(false);
    setShowLinkPopup(false);
    setShowBgPopup(false);
  };

  /* =========================================================
     OUTSIDE CLICK
  ========================================================= */

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsidePopup =
        popupRef.current && popupRef.current.contains(event.target);

      const clickedInsideToolbar =
        toolbarRef.current && toolbarRef.current.contains(event.target);

      if (clickedInsidePopup) {
        return;
      }

      if (!clickedInsideToolbar) {
        closeAllPopups();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* =========================================================
     ADD BOX
  ========================================================= */

  const addBox = () => {
    if (previewMode) return;

    const boxes = elements.filter((el) => el.type === "box");

    let newX = 20;

    if (boxes.length > 0) {
      const lastBox = boxes[boxes.length - 1];

      newX = lastBox.x + lastBox.width + 20;
    }

    const newBox = {
      id: Date.now(),

      type: "box",

      x: newX,
      y: 60,

      width: 300,
      height: 200,

      background: "#ffffff",

      backgroundImage: null,

      backgroundPosition: "center center",

      backgroundSize: "cover",

      backgroundRepeat: "no-repeat",

      borderRadius: 10,

      padding: 10,

      opacity: 1,

      rotation: 0,

      scaleX: 1,
      scaleY: 1,

      zIndex: 1,
    };

    setElements((prev) => [...prev, newBox]);

    closeAllPopups();
  };

  /* =========================================================
     ADD TEXT
  ========================================================= */

  const addHeading = () => {
    if (previewMode) return;

    const box = getSelectedBox();

    if (!box) {
      toast.warning("Please select a box first");

      return;
    }

    const text = {
      id: Date.now(),

      type: "heading",

      parentBox: box.id,

      text: "Edit Text",

      x: box.x + 20,
      y: box.y + 20,

      width: 200,
      height: 60,

      fontSize: "28px",

      color: "#000000",

      fontFamily: "Inter",

      fontWeight: "600",

      fontStyle: "normal",

      textDecoration: "none",

      textAlign: "left",

      opacity: 1,

      rotation: 0,

      scaleX: 1,
      scaleY: 1,

      borderRadius: 0,

      padding: 0,

      zIndex: 3,
    };

    setElements((prev) => [...prev, text]);

    closeAllPopups();
  };

  /* =========================================================
     ADD BUTTON
  ========================================================= */

  const addButton = () => {
    if (previewMode) return;

    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      return;
    }

    const button = {
      id: Date.now(),

      type: "button",

      parentBox: box.id,

      text: "Click Me",

      link: "",

      x: box.x + 40,
      y: box.y + 40,

      width: 160,
      height: 50,

      background: "#000000",

      color: "#ffffff",

      borderRadius: 8,

      padding: 0,

      fontSize: "16px",

      fontWeight: "500",

      fontStyle: "normal",

      textDecoration: "none",

      fontFamily: "Inter",

      textAlign: "center",

      opacity: 1,

      rotation: 0,

      scaleX: 1,
      scaleY: 1,

      zIndex: 2,
    };

    setElements((prev) => [...prev, button]);

    closeAllPopups();
  };

  /* =========================================================
     OPEN IMAGE PICKER
  ========================================================= */

  const openImagePicker = () => {
    if (previewMode) return;

    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      return;
    }

    fileInputRef.current?.click();
  };

  /* =========================================================
     IMAGE UPLOAD
  ========================================================= */

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      e.target.value = "";

      return;
    }

    /*
      Basic image validation
    */

    if (!file.type?.startsWith("image/")) {
      toast.warning("Please select an image file");

      e.target.value = "";

      return;
    }

    try {
      const dataURL = await fileToDataURL(file);

      const img = {
        id: Date.now(),

        type: "image",

        parentBox: box.id,

        src: dataURL,

        link: "",

        x: box.x + 20,
        y: box.y + 20,

        width: Math.min(200, Math.max(50, box.width - 40)),

        height: Math.min(200, Math.max(40, box.height - 40)),

        borderRadius: 0,

        padding: 0,

        opacity: 1,

        rotation: 0,

        scaleX: 1,
        scaleY: 1,

        zIndex: 2,
      };

      setElements((prev) => [...prev, img]);

      setShowImagePopup(false);

      toast.success("Image added successfully");
    } catch (error) {
      console.log("Image upload error:", error);

      toast.error("Unable to process image");
    } finally {
      e.target.value = "";
    }
  };

  const setBackgroundImage = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      e.target.value = "";

      return;
    }

    if (!file.type?.startsWith("image/")) {
      toast.warning("Please select an image file");

      e.target.value = "";

      return;
    }

    try {
      const dataURL = await fileToDataURL(file);

      setElements((prev) =>
        prev.map((el) =>
          el.id === selectedId
            ? {
                ...el,

                backgroundImage: dataURL,

                backgroundPosition: "center center",

                backgroundSize: "cover",

                backgroundRepeat: "no-repeat",
              }
            : el,
        ),
      );

      setShowBgPopup(false);

      toast.success("Background image added");
    } catch (error) {
      console.log("Background image error:", error);

      toast.error("Unable to process background image");
    } finally {
      if (bgImageRef.current) {
        bgImageRef.current.value = "";
      }
    }
  };

  /* =========================================================
     ADD IMAGE USING URL
  ========================================================= */

  const handleAddImageURL = () => {
    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      return;
    }

    const url = imageURL.trim();

    if (!url) {
      toast.warning("Please enter image URL");

      return;
    }

    const img = {
      id: Date.now(),

      type: "image",

      parentBox: box.id,

      src: url,

      link: "",

      x: box.x + 20,
      y: box.y + 20,

      width: Math.min(200, Math.max(50, box.width - 40)),

      height: Math.min(200, Math.max(40, box.height - 40)),

      borderRadius: 0,

      padding: 0,

      opacity: 1,

      rotation: 0,

      scaleX: 1,
      scaleY: 1,

      zIndex: 2,
    };

    setElements((prev) => [...prev, img]);

    setImageURL("");

    setShowImagePopup(false);
  };

  /* =========================================================
     ADD BACKGROUND USING URL
  ========================================================= */

  const handleBackgroundURL = () => {
    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      return;
    }

    const url = bgURL.trim();

    if (!url) {
      toast.warning("Please enter background image URL");

      return;
    }

    setElements((prev) =>
      prev.map((el) =>
        el.id === selectedId
          ? {
              ...el,

              backgroundImage: url,

              backgroundPosition: "center center",

              backgroundSize: "cover",

              backgroundRepeat: "no-repeat",
            }
          : el,
      ),
    );

    setBgURL("");

    setShowBgPopup(false);
  };

  /* =========================================================
     APPLY LINK
  ========================================================= */

  const applyLink = () => {
    if (!selectedId) {
      toast.warning("Select an element first");

      return;
    }

    const selectedElement = elements.find((el) => el.id === selectedId);

    if (!selectedElement) {
      return;
    }

    setElements((prev) =>
      prev.map((el) =>
        el.id === selectedId
          ? {
              ...el,
              link: linkValue.trim(),
            }
          : el,
      ),
    );

    setLinkValue("");

    setShowLinkPopup(false);
  };

  /* =========================================================
     OPEN LINK POPUP
  ========================================================= */

  const handleLinkPopup = () => {
    if (previewMode) return;

    if (!selectedId) {
      toast.warning("Select an element first");

      return;
    }

    const selectedElement = elements.find((el) => el.id === selectedId);

    /*
      Existing link ko input me show karenge.
    */

    setLinkValue(selectedElement?.link || "");

    setShowImagePopup(false);
    setShowBgPopup(false);

    setShowLinkPopup((prev) => !prev);
  };

  /* =========================================================
     OPEN IMAGE POPUP
  ========================================================= */

  const handleImagePopup = () => {
    if (previewMode) return;

    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      return;
    }

    setShowLinkPopup(false);
    setShowBgPopup(false);

    setShowImagePopup((prev) => !prev);
  };

  /* =========================================================
     OPEN BACKGROUND POPUP
  ========================================================= */

  const handleBgPopup = () => {
    if (previewMode) return;

    const box = getSelectedBox();

    if (!box) {
      toast.warning("Select a box first");

      return;
    }

    setShowLinkPopup(false);
    setShowImagePopup(false);

    setShowBgPopup((prev) => !prev);
  };

  return (
    <>
      <NoIndex />

      <div className="tool-bar montserrat-font-1" ref={toolbarRef}>
        {/* =========================================
            ADD BOX
        ========================================= */}

        <button
          type="button"
          className="toolls-btn"
          onClick={addBox}
          disabled={previewMode}
          title="Add Box"
        >
          <FaSquare />
        </button>

        {/* =========================================
            ADD TEXT
        ========================================= */}

        <button
          type="button"
          className="toolls-btn montserrat-font-1"
          onClick={addHeading}
          disabled={previewMode}
          title="Add Text"
        >
          T
        </button>

        {/* =========================================
            IMAGE
        ========================================= */}

        <div
          className="linkk-tooll montserrat-font-1"
          style={{
            position: "relative",
          }}
        >
          <button
            type="button"
            className="toolls-btn montserrat-font-1"
            onClick={handleImagePopup}
            disabled={previewMode}
            title="Add Image"
          >
            <FaImage />
          </button>

          {showImagePopup && (
            <div className="linkk-popupp montserrat-font-1" ref={popupRef}>
              <input
                type="text"
                placeholder="Paste Image URL"
                value={imageURL}
                onChange={(e) => setImageURL(e.target.value)}
              />

              <button type="button" onClick={handleAddImageURL}>
                Add
              </button>

              <button type="button" onClick={openImagePicker}>
                Upload
              </button>
            </div>
          )}
        </div>

        {/* =========================================
            BUTTON
        ========================================= */}

        <button
          type="button"
          className="toolls-btn montserrat-font-1"
          onClick={addButton}
          disabled={previewMode}
          title="Add Button"
        >
          [+]
        </button>

        {/* =========================================
            BACKGROUND
        ========================================= */}

        <div
          className="linkk-tooll montserrat-font-1"
          style={{
            position: "relative",
          }}
        >
          <button
            type="button"
            className="toolls-btn montserrat-font-1"
            onClick={handleBgPopup}
            disabled={previewMode}
            title="Background Image"
          >
            <FaCamera />
          </button>

          {showBgPopup && (
            <div className="linkk-popupp montserrat-font-1" ref={popupRef}>
              <input
                type="text"
                placeholder="Paste Background Image URL"
                value={bgURL}
                onChange={(e) => setBgURL(e.target.value)}
              />

              <button type="button" onClick={handleBackgroundURL}>
                Apply
              </button>

              <button
                type="button"
                onClick={() => {
                  bgImageRef.current?.click();
                }}
              >
                Upload
              </button>
            </div>
          )}
        </div>

        {/* =========================================
            LINK
        ========================================= */}

        <div
          className="linkk-tooll montserrat-font-1"
          style={{
            position: "relative",
          }}
        >
          <button
            type="button"
            className="toolls-btn"
            onClick={handleLinkPopup}
            disabled={previewMode}
            title="Add Link"
          >
            <FaLink />
          </button>

          {showLinkPopup && (
            <div className="linkk-popupp montserrat-font-1" ref={popupRef}>
              <input
                type="text"
                placeholder="Enter URL"
                value={linkValue}
                onChange={(e) => setLinkValue(e.target.value)}
              />

              <button type="button" onClick={applyLink}>
                Apply
              </button>
            </div>
          )}
        </div>

        {/* =========================================
            HIDDEN IMAGE INPUT
        ========================================= */}

        <input
          type="file"
          ref={fileInputRef}
          style={{
            display: "none",
          }}
          accept="image/*"
          onChange={handleUpload}
        />

        {/* =========================================
            HIDDEN BACKGROUND INPUT
        ========================================= */}

        <input
          type="file"
          ref={bgImageRef}
          style={{
            display: "none",
          }}
          accept="image/*"
          onChange={setBackgroundImage}
        />
      </div>
    </>
  );
};

export default Toolbar;
